import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  BarChart3, TrendingUp, TrendingDown, Users, Activity,
  Heart, Clock, Star, DollarSign, FileText, Download,
  Calendar, AlertTriangle, CheckCircle, PieChart,
  ArrowUpRight, ArrowDownRight, Loader2, RefreshCw
} from "lucide-react";

/**
 * Advanced Reporting & Analytics Dashboard
 * Comprehensive analytics with customizable reports
 */
export default function AnalyticsDashboard() {
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState("30days");
  const [activeTab, setActiveTab] = useState("executive");
  const [dashboardData, setDashboardData] = useState(null);
  const [patientData, setPatientData] = useState(null);
  const [staffData, setStaffData] = useState(null);
  const [financialData, setFinancialData] = useState(null);
  const [qualityData, setQualityData] = useState(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const [exec, patient, staff, financial, quality] = await Promise.all([
        api.get(`/analytics/executive-dashboard?date_range=${dateRange}`),
        api.get(`/analytics/patient-analytics?date_range=${dateRange}`),
        api.get(`/analytics/staff-analytics?date_range=${dateRange}`),
        api.get(`/analytics/financial-analytics?date_range=${dateRange}`),
        api.get(`/analytics/quality-metrics?date_range=${dateRange}`)
      ]);

      setDashboardData(exec.data);
      setPatientData(patient.data);
      setStaffData(staff.data);
      setFinancialData(financial.data);
      setQualityData(quality.data);
    } catch (error) {
      console.error("Error loading analytics:", error);
      toast.error("Failed to load analytics data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [dateRange]);

  const handleExportReport = async (reportType) => {
    try {
      const response = await api.post("/analytics/generate-report", {
        report_type: reportType,
        date_range: dateRange,
        include_charts: true,
        include_trends: true,
        format: "html"
      });

      if (response.data?.html) {
        const printWindow = window.open("", "_blank");
        printWindow.document.write(response.data.html);
        printWindow.document.close();
        toast.success("Report generated! Use Print > Save as PDF");
      }
    } catch (error) {
      toast.error("Failed to generate report");
    }
  };

  if (loading) {
    return (
      <div className="min-h-[600px] flex items-center justify-center">
        <div className="text-center space-y-3">
          <Loader2 className="w-12 h-12 animate-spin text-[#4A7C6F] mx-auto" />
          <p className="text-slate-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  const kpis = dashboardData?.kpis || {};

  return (
    <div className="container mx-auto px-4 py-8 space-y-6" data-testid="analytics-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Analytics & Reports</h1>
          <p className="text-slate-600">Comprehensive insights for Goodwin Hospice</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7days">Last 7 Days</SelectItem>
              <SelectItem value="30days">Last 30 Days</SelectItem>
              <SelectItem value="90days">Last 90 Days</SelectItem>
              <SelectItem value="12months">Last 12 Months</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={loadData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <KPICard
          title="Active Patients"
          value={kpis.active_patients?.value || 0}
          change={kpis.active_patients?.change}
          trend={kpis.active_patients?.trend}
          icon={Users}
        />
        <KPICard
          title="Staff Members"
          value={kpis.staff_members?.value || 0}
          change={kpis.staff_members?.change}
          trend={kpis.staff_members?.trend}
          icon={Heart}
        />
        <KPICard
          title="Scheduled Visits"
          value={kpis.scheduled_visits?.value || 0}
          change={kpis.scheduled_visits?.change}
          trend={kpis.scheduled_visits?.trend}
          icon={Calendar}
        />
        <KPICard
          title="Satisfaction"
          value={`${kpis.patient_satisfaction?.value || 0}%`}
          change={kpis.patient_satisfaction?.change}
          trend={kpis.patient_satisfaction?.trend}
          icon={Star}
        />
        <KPICard
          title="Response Time"
          value={kpis.avg_response_time?.value || "N/A"}
          change={kpis.avg_response_time?.change}
          trend={kpis.avg_response_time?.trend}
          icon={Clock}
          invertTrend
        />
        <KPICard
          title="Quality Score"
          value={`${kpis.care_quality_score?.value || 0}/5`}
          change={kpis.care_quality_score?.change}
          trend={kpis.care_quality_score?.trend}
          icon={Activity}
        />
      </div>

      {/* Tabs for Different Analytics */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex items-center justify-between">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="executive">Executive</TabsTrigger>
            <TabsTrigger value="patients">Patients</TabsTrigger>
            <TabsTrigger value="staff">Staff</TabsTrigger>
            <TabsTrigger value="financial">Financial</TabsTrigger>
            <TabsTrigger value="quality">Quality</TabsTrigger>
          </TabsList>
          <Button onClick={() => handleExportReport(activeTab)} variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Export Report
          </Button>
        </div>

        {/* Executive Dashboard */}
        <TabsContent value="executive" className="space-y-6 mt-6">
          <div className="grid lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                  Alerts Overview
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-red-50 rounded-lg">
                    <p className="text-3xl font-bold text-red-600">{dashboardData?.alerts?.critical || 0}</p>
                    <p className="text-sm text-red-700">Critical</p>
                  </div>
                  <div className="text-center p-4 bg-amber-50 rounded-lg">
                    <p className="text-3xl font-bold text-amber-600">{dashboardData?.alerts?.warning || 0}</p>
                    <p className="text-sm text-amber-700">Warning</p>
                  </div>
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <p className="text-3xl font-bold text-blue-600">{dashboardData?.alerts?.info || 0}</p>
                    <p className="text-sm text-blue-700">Info</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-[#4A7C6F]" />
                  Quick Stats
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  {Object.entries(dashboardData?.quick_stats || {}).map(([key, value]) => (
                    <div key={key} className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-lg font-semibold text-slate-800">{value}</p>
                      <p className="text-xs text-slate-500 capitalize">{key.replace(/_/g, " ")}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Patient Analytics */}
        <TabsContent value="patients" className="space-y-6 mt-6">
          <div className="grid lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Diagnosis Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {patientData?.demographics?.diagnosis_distribution?.map((diag) => (
                    <div key={diag.diagnosis} className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-700">{diag.diagnosis}</span>
                        <span className="text-slate-500">{diag.count} ({diag.percentage}%)</span>
                      </div>
                      <Progress 
                        value={diag.percentage} 
                        className="h-2"
                        style={{ "--progress-background": diag.color }}
                      />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Patient Outcomes</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <MetricBox 
                    label="Avg Length of Stay" 
                    value={`${patientData?.outcomes?.avg_length_of_stay || 0} days`}
                  />
                  <MetricBox 
                    label="Peaceful Deaths" 
                    value={`${patientData?.outcomes?.peaceful_deaths_percentage || 0}%`}
                  />
                  <MetricBox 
                    label="Symptom Control" 
                    value={`${patientData?.outcomes?.symptom_control_score || 0}/10`}
                  />
                  <MetricBox 
                    label="Family Satisfaction" 
                    value={`${patientData?.outcomes?.family_satisfaction || 0}%`}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Age Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {patientData?.demographics?.age_distribution?.map((age) => (
                    <div key={age.range} className="flex items-center gap-4">
                      <span className="w-16 text-sm text-slate-600">{age.range}</span>
                      <div className="flex-1 bg-slate-100 rounded-full h-6 overflow-hidden">
                        <div 
                          className="h-full bg-[#4A7C6F] rounded-full flex items-center justify-end pr-2"
                          style={{ width: `${age.percentage}%` }}
                        >
                          <span className="text-xs text-white font-medium">{age.count}</span>
                        </div>
                      </div>
                      <span className="w-12 text-sm text-slate-500">{age.percentage}%</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Referral Sources</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {patientData?.referral_sources?.map((source) => (
                    <div key={source.source} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <span className="font-medium text-slate-700">{source.source}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-slate-600">{source.count}</span>
                        <Badge variant="outline">{source.percentage}%</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Staff Analytics */}
        <TabsContent value="staff" className="space-y-6 mt-6">
          <div className="grid lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Staff Performance</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <MetricBox 
                    label="Avg Visits/Day" 
                    value={staffData?.performance?.avg_visits_per_day || 0}
                  />
                  <MetricBox 
                    label="Doc Compliance" 
                    value={`${staffData?.performance?.documentation_compliance || 0}%`}
                  />
                  <MetricBox 
                    label="On-Time Arrival" 
                    value={`${staffData?.performance?.on_time_arrival || 0}%`}
                  />
                  <MetricBox 
                    label="Patient Rating" 
                    value={`${staffData?.performance?.patient_satisfaction_avg || 0}/5`}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Top Performers</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {staffData?.top_performers?.map((performer, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#4A7C6F] text-white flex items-center justify-center font-bold">
                          {idx + 1}
                        </div>
                        <div>
                          <p className="font-medium text-slate-700">{performer.name}</p>
                          <p className="text-xs text-slate-500">{performer.role}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-[#4A7C6F]">{performer.score}%</p>
                        <p className="text-xs text-slate-500">{performer.visits} visits</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Workforce Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  {staffData?.workforce?.by_role?.map((role) => (
                    <div key={role.role} className="text-center p-4 bg-slate-50 rounded-lg">
                      <p className="text-2xl font-bold text-[#4A7C6F]">{role.count}</p>
                      <p className="text-xs text-slate-600">{role.role}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Financial Analytics */}
        <TabsContent value="financial" className="space-y-6 mt-6">
          <div className="grid lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Revenue by Payer</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {financialData?.revenue?.by_payer?.map((payer) => (
                    <div key={payer.payer} className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-700">{payer.payer}</span>
                        <span className="text-slate-600">${(payer.amount / 1000).toFixed(0)}K ({payer.percentage}%)</span>
                      </div>
                      <Progress value={payer.percentage} className="h-2" />
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t">
                  <div className="flex justify-between">
                    <span className="font-medium text-slate-700">Total Revenue</span>
                    <span className="font-bold text-[#4A7C6F]">
                      ${((financialData?.revenue?.total || 0) / 1000).toFixed(0)}K
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Billing Metrics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <MetricBox 
                    label="Claims Submitted" 
                    value={financialData?.billing?.claims_submitted || 0}
                  />
                  <MetricBox 
                    label="Claims Paid" 
                    value={financialData?.billing?.claims_paid || 0}
                  />
                  <MetricBox 
                    label="Denial Rate" 
                    value={`${financialData?.billing?.denial_rate || 0}%`}
                  />
                  <MetricBox 
                    label="Collection Rate" 
                    value={`${financialData?.billing?.collection_rate || 0}%`}
                  />
                </div>
              </CardContent>
            </Card>

            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Profitability</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-6">
                  <div className="text-center p-6 bg-green-50 rounded-xl">
                    <p className="text-4xl font-bold text-green-600">
                      {financialData?.profitability?.gross_margin || 0}%
                    </p>
                    <p className="text-sm text-green-700">Gross Margin</p>
                  </div>
                  <div className="text-center p-6 bg-blue-50 rounded-xl">
                    <p className="text-4xl font-bold text-blue-600">
                      {financialData?.profitability?.net_margin || 0}%
                    </p>
                    <p className="text-sm text-blue-700">Net Margin</p>
                  </div>
                  <div className="text-center p-6 bg-purple-50 rounded-xl">
                    <p className="text-4xl font-bold text-purple-600">
                      ${(financialData?.profitability?.revenue_per_patient || 0).toLocaleString()}
                    </p>
                    <p className="text-sm text-purple-700">Revenue/Patient</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Quality Metrics */}
        <TabsContent value="quality" className="space-y-6 mt-6">
          <div className="grid lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>CAHPS Scores</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(qualityData?.cahps_scores || {}).map(([key, value]) => (
                    <div key={key} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <span className="capitalize text-slate-700">{key.replace(/_/g, " ")}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-24 bg-slate-200 rounded-full h-2">
                          <div 
                            className="bg-[#4A7C6F] h-2 rounded-full"
                            style={{ width: `${(value / 5) * 100}%` }}
                          />
                        </div>
                        <span className="font-medium text-[#4A7C6F]">{value}/5</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Quality Measures</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <MetricBox 
                    label="Pain Assessment" 
                    value={`${qualityData?.hospice_quality?.pain_assessment_rate || 0}%`}
                  />
                  <MetricBox 
                    label="Dyspnea Treatment" 
                    value={`${qualityData?.hospice_quality?.dyspnea_treatment_rate || 0}%`}
                  />
                  <MetricBox 
                    label="Bowel Regimen" 
                    value={`${qualityData?.hospice_quality?.bowel_regimen_rate || 0}%`}
                  />
                  <MetricBox 
                    label="Beliefs Addressed" 
                    value={`${qualityData?.hospice_quality?.beliefs_addressed_rate || 0}%`}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Compliance</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(qualityData?.compliance || {}).map(([key, value]) => (
                    <div key={key} className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="capitalize text-slate-700">{key.replace(/_/g, " ")}</span>
                        <span className="text-[#4A7C6F] font-medium">{value}%</span>
                      </div>
                      <Progress value={value} className="h-2" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Incidents Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-slate-50 rounded-lg">
                    <p className="text-3xl font-bold text-slate-800">{qualityData?.incidents?.total || 0}</p>
                    <p className="text-sm text-slate-600">Total Incidents</p>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <p className="text-3xl font-bold text-green-600">{qualityData?.incidents?.severity?.minor || 0}</p>
                    <p className="text-sm text-green-700">Minor</p>
                  </div>
                </div>
                <div className="mt-4 p-4 bg-[#E8F4F0] rounded-lg">
                  <p className="text-sm text-[#4A7C6F]">
                    <CheckCircle className="w-4 h-4 inline mr-1" />
                    Your quality metrics rank in the <strong>87th percentile</strong> nationally
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function KPICard({ title, value, change, trend, icon: Icon, invertTrend = false }) {
  const isPositive = invertTrend ? trend === "down" : trend === "up";
  
  return (
    <Card className="border-0 shadow-sm">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="w-10 h-10 rounded-lg bg-[#E8F4F0] flex items-center justify-center">
            <Icon className="w-5 h-5 text-[#4A7C6F]" />
          </div>
          {change !== undefined && (
            <div className={`flex items-center text-xs ${isPositive ? "text-green-600" : "text-red-600"}`}>
              {isPositive ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
              {Math.abs(change)}%
            </div>
          )}
        </div>
        <p className="text-2xl font-bold text-slate-800 mt-3">{value}</p>
        <p className="text-xs text-slate-500">{title}</p>
      </CardContent>
    </Card>
  );
}

function MetricBox({ label, value }) {
  return (
    <div className="p-3 bg-slate-50 rounded-lg text-center">
      <p className="text-lg font-bold text-[#4A7C6F]">{value}</p>
      <p className="text-xs text-slate-600">{label}</p>
    </div>
  );
}
